import React from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import RangeDatePicker from '@components/Utilities/DatePicker/RangeDatePicker';
import { DeliveryType, OrderStatus, orderStatusTranslations } from '@api/Orders/Order';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { UserType } from '@api/Users/User';
import { SalesChannel } from '@api/SalesChannels/SalesChannel';

export interface FilterValues {
    startDate: Date;
    endDate: Date;
    status: string;
    branchOfficeId: string;
    deliveryType: string;
    hasUber: string;
    collaboratorId: string;
    // Solo usado por Reporte de Ventas (el sidebar lo muestra si recibe salesChannels)
    salesChannel?: string;
}

interface FilterSidebarProps {
    filters: FilterValues;
    onFilterChange: (newFilters: FilterValues) => void;
    branchOffices: BranchOffice[];
    collaborators: UserType[];
    /** Si se pasa, muestra el filtro de canal de venta (exclusivo con tipo de entrega) */
    salesChannels?: SalesChannel[];
    /**
     * false para usuarios con sucursales asignadas específicas: la opción "Todas"
     * solo aparece si tienen más de una asignada (y significa "todas las suyas").
     */
    allowAllBranches?: boolean;
    /** Sin la opción "Todas las sucursales" aunque tenga varias (el admin de sucursal en el Dashboard). */
    forbidAllBranches?: boolean;
    /** false cuando la pantalla está amarrada a la sucursal en operación (ej. cortes de caja) */
    showBranchFilter?: boolean;
    /** false cuando el estatus es fijo (ej. cortes de caja: solo Completado) */
    showStatusFilter?: boolean;
    onClear: () => void;
    onApply: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
    filters,
    onFilterChange,
    branchOffices,
    collaborators,
    salesChannels,
    allowAllBranches = true,
    forbidAllBranches = false,
    showBranchFilter = true,
    showStatusFilter = true,
    onClear,
    onApply
}) => {
    const showAllBranchesOption = !forbidAllBranches && (allowAllBranches || branchOffices.length > 1);

    return (
        <Stack spacing={3} height="100%">
            <Typography variant="h6" fontWeight={600}>
                Filtros
            </Typography>

            <RangeDatePicker
                fullWidth
                value={format(filters.startDate, 'dd MMMM, yyy', { locale: es })}
                onChangeStartDate={(startDate) => onFilterChange({ ...filters, startDate })}
                onChangeEndDate={(endDate) => onFilterChange({ ...filters, endDate })}
                onChangeRange={(startDate, endDate) => onFilterChange({ ...filters, startDate, endDate })}
            />

            {showBranchFilter && (
                <FormControl fullWidth>
                    <InputLabel id="sucursal-label">Sucursal</InputLabel>
                    <Select
                        labelId="sucursal-label"
                        value={filters.branchOfficeId}
                        onChange={(e: SelectChangeEvent) =>
                            onFilterChange({ ...filters, branchOfficeId: e.target.value })
                        }
                        label="Sucursal"
                    >
                        {showAllBranchesOption && <MenuItem value="all">Todas</MenuItem>}
                        {branchOffices.map((office) => (
                            <MenuItem key={office.id} value={office.id}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {salesChannels && (
                <FormControl fullWidth>
                    <InputLabel id="canal-label">Canal de venta</InputLabel>
                    <Select
                        labelId="canal-label"
                        value={filters.salesChannel || 'all'}
                        onChange={(e: SelectChangeEvent) =>
                            // Canal y tipo de entrega son excluyentes: las órdenes WEB tienen
                            // deliveryType, las de canal tienen salesChannel.
                            onFilterChange({
                                ...filters,
                                salesChannel: e.target.value,
                                deliveryType: e.target.value !== 'all' ? 'all' : filters.deliveryType
                            })
                        }
                        label="Canal de venta"
                    >
                        <MenuItem value="all">Todos</MenuItem>
                        {salesChannels.map((salesChannel) => (
                            <MenuItem key={salesChannel.id} value={salesChannel.id}>
                                {salesChannel.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {showStatusFilter && (
                <FormControl fullWidth>
                    <InputLabel id="estatus-label">Estatus</InputLabel>
                    <Select
                        labelId="estatus-label"
                        value={filters.status}
                        onChange={(e: SelectChangeEvent) => onFilterChange({ ...filters, status: e.target.value })}
                        label="Estatus"
                    >
                        <MenuItem value="all">Todos</MenuItem>
                        {Object.entries(OrderStatus).map(([value]) => (
                            <MenuItem key={value} value={value}>
                                {orderStatusTranslations[value] || value}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <FormControl fullWidth>
                <InputLabel id="entrega-label">Entrega</InputLabel>
                <Select
                    labelId="entrega-label"
                    value={filters.deliveryType}
                    onChange={(e: SelectChangeEvent) =>
                        onFilterChange({
                            ...filters,
                            deliveryType: e.target.value,
                            salesChannel:
                                salesChannels && e.target.value !== 'all' ? 'all' : filters.salesChannel
                        })
                    }
                    label="Entrega"
                >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value={DeliveryType.Pickup}>Pickup</MenuItem>
                    <MenuItem value={DeliveryType.Delivery}>Delivery</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth>
                <InputLabel id="uber-label">¿Envío Uber?</InputLabel>
                <Select
                    labelId="uber-label"
                    value={filters.hasUber}
                    onChange={(e: SelectChangeEvent) => onFilterChange({ ...filters, hasUber: e.target.value })}
                    label="¿Envío Uber?"
                >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="yes">Sí, con Uber</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth>
                <InputLabel id="colaborador-label">Colaborador</InputLabel>
                <Select
                    labelId="colaborador-label"
                    value={filters.collaboratorId}
                    onChange={(e: SelectChangeEvent) => onFilterChange({ ...filters, collaboratorId: e.target.value })}
                    label="Colaborador"
                >
                    <MenuItem value="all">Todos</MenuItem>
                    {collaborators.map((collaborator) => (
                        <MenuItem key={collaborator.uid} value={collaborator.uid}>
                            {collaborator.profile.firstname} {collaborator.profile.lastname}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ mt: 'auto' }}>
                <Button fullWidth variant="outlined" color="error" startIcon={<Clear />} onClick={onClear}>
                    Borrar
                </Button>
                <Button fullWidth variant="contained" color="primary" onClick={onApply}>
                    Aplicar
                </Button>
            </Stack>
        </Stack>
    );
};

export default FilterSidebar;
